import { rateioFixaUnit } from '@favo/core';

import type { FatiaCascata } from '@/componentes/cascata-preco';
import { Valor } from '@/componentes/valor';
import type { Cenario, Parametros } from '@/dados';
import { formatarMoeda, formatarNumero } from '@/design/formato';

/* memoria-de-calculo.tsx — de onde vem cada real, em UM lugar só.
 *
 * A cascata do preço e o DRE do projeto existem em duas superfícies: o painel,
 * onde o integrador decide, e o laudo, que ele imprime e leva. As duas têm de
 * mostrar o mesmo número, e o jeito de garantir isso não é disciplina: é as
 * duas lerem a MESMA derivação.
 *
 * Foi exatamente a duplicação de motor de cálculo que fez o dashboard e o PDF
 * mostrarem números diferentes na versão anterior. A conta em si já mora no
 * `core`; o que mora aqui é a decomposição de EXIBIÇÃO, que o motor entrega
 * agregada: separar do custo direto total a parte que é kit da que é serviço,
 * escolher o denominador da cascata, e ordenar os lançamentos do demonstrativo.
 *
 * Aqui não se calcula margem, preço nem rateio. Se aparecer uma conta dessas
 * neste arquivo, ela está no lugar errado e o lugar é o `core`.
 */

/** Uma linha do demonstrativo. `null` é ausência de dado, nunca zero. */
export type Lancamento = {
  nome: string;
  nota?: string;
  projeto: number | null;
  mes: number | null;
  operador?: 'menos' | 'igual' | 'nenhum';
  campo?: 'nenhum' | 'areia' | 'mint' | 'telha';
  total?: boolean;
  perda?: boolean;
};

/**
 * As fatias da cascata, e a escolha de denominador que elas carregam.
 *
 * Com margem positiva o denominador é o PREÇO e as fatias somam exatamente
 * ele. Com margem negativa o custo passa do preço e não existe repartição do
 * preço que caiba: o denominador vira o CUSTO TOTAL, e quem exibe tem de dizer
 * isso antes do primeiro número. Por isso a função devolve `margemPositiva`
 * junto: a decisão de denominador é da derivação, e o texto que a explica não
 * pode divergir dela.
 */
export function fatiasDoPreco(
  p: Parametros,
  atual: Cenario,
): { fatias: FatiaCascata[]; margemPositiva: boolean; custoTotal: number } {
  const custoServico = p.cdt - p.kit;
  const margemPositiva = atual.mcUnit >= 0;
  const custoTotal = p.kit + custoServico + atual.dvUnit;
  const base = margemPositiva ? p.ticket : custoTotal;
  const pct = (valor: number): number => (base > 0 ? (valor / base) * 100 : 0);

  const fatias: FatiaCascata[] = [
    {
      id: 'kit',
      nome: 'KIT',
      nota: 'módulos, inversor e estrutura',
      valor: <Valor valor={p.kit} formato="moeda" />,
      pct: pct(p.kit),
      resta: formatarMoeda(atual.baseServico),
    },
    {
      id: 'servico',
      nome: 'Custos de serviço',
      nota: 'instalação, projeto, ART e homologação',
      valor: <Valor valor={custoServico} formato="moeda" />,
      pct: pct(custoServico),
      resta: formatarMoeda(atual.baseServico - custoServico),
    },
    {
      id: 'variaveis',
      nome: 'Despesas variáveis',
      nota: 'comissão, tributo e percentuais sobre a venda',
      valor: <Valor valor={atual.dvUnit} formato="moeda" />,
      pct: pct(atual.dvUnit),
      ...(margemPositiva ? { resta: formatarMoeda(atual.mcUnit) } : {}),
    },
  ];

  if (margemPositiva) {
    fatias.push({
      id: 'margem',
      nome: 'Margem de contribuição',
      nota: 'o que fica antes da estrutura',
      valor: <Valor valor={atual.mcUnit} formato="moeda" />,
      pct: pct(atual.mcUnit),
      acento: true,
    });
  }

  return { fatias, margemPositiva, custoTotal };
}

/**
 * Os lançamentos do demonstrativo, do preço até o resultado.
 *
 * As linhas de CUSTO existem sempre, porque saem de `p`. As de receita e
 * resultado dependem do cenário, e sem ele viram ausência: `null`, que a tela
 * desenha como travessão, nunca zero mascarando "não sei".
 */
export function lancamentosDoProjeto(
  p: Parametros,
  atual: Cenario | null,
  semPreco: boolean,
): Lancamento[] {
  const custoServico = p.cdt - p.kit;
  // Sai de `p`, não do cenário: é V12/12, e as linhas de custo continuam
  // existindo mesmo quando o motor recusa opinar sobre o preço.
  const qtdMes = p.V12 / 12;
  const porMes = (v: number | null): number | null => (v === null ? null : v * qtdMes);
  const comPreco = (v: number | null | undefined): number | null =>
    semPreco || atual === null || v === undefined ? null : v;
  /** Inverte o sinal preservando a ausência: lançamento é saída, não zero. */
  const menos = (v: number | undefined): number | null => (v === undefined ? null : -v);

  const linhas: Lancamento[] = [
    {
      nome: 'Receita',
      nota: 'preço de venda',
      projeto: comPreco(atual?.ticket),
      mes: comPreco(atual?.mensal.faturamento),
      operador: 'nenhum',
      campo: 'areia',
      total: true,
    },
    { nome: 'Custo do KIT', projeto: -p.kit, mes: porMes(-p.kit) },
    { nome: 'Custos de serviço', projeto: -custoServico, mes: porMes(-custoServico) },
    {
      nome: 'Comissão de vendas',
      projeto: comPreco(menos(atual?.comissaoR)),
      mes: comPreco(porMes(menos(atual?.comissaoR))),
    },
    {
      nome: 'Carga tributária',
      projeto: comPreco(menos(atual?.tributoR)),
      mes: comPreco(porMes(menos(atual?.tributoR))),
    },
  ];

  for (const extra of atual?.extrasDetalhes ?? []) {
    linhas.push({
      nome: extra.nome,
      projeto: comPreco(-extra.v),
      mes: comPreco(porMes(-extra.v)),
    });
  }
  if (p.outras > 0) {
    linhas.push({
      nome: 'Percentual adicional',
      projeto: comPreco(menos(atual?.outrasR)),
      mes: comPreco(porMes(menos(atual?.outrasR))),
    });
  }

  const mcNegativa = !semPreco && atual != null && atual.mcUnit < 0;
  linhas.push({
    nome: 'Margem de contribuição',
    projeto: comPreco(atual?.mcUnit),
    mes: comPreco(atual?.mensal.mc),
    operador: 'igual',
    campo: mcNegativa ? 'telha' : 'mint',
    total: true,
    perda: mcNegativa,
  });
  // O rateio sai de `p`, não do cenário: despesa fixa e volume existem sem o
  // preço do projeto. Amarrá-lo ao cenário fazia a nota pedir volume que o
  // usuário JÁ informou quando o que faltava era o preço.
  const fixUnit = rateioFixaUnit(p);
  linhas.push({
    nome: 'Despesas fixas',
    nota:
      fixUnit == null
        ? 'informe o volume para ratear'
        : `rateio: despesa fixa anual dividida por ${formatarNumero(p.V12, { casasMax: 1 })} projetos no ano`,
    projeto: fixUnit == null ? null : -fixUnit,
    mes: p.despFixa > 0 ? -p.despFixa : null,
  });

  const resultadoNegativo = !semPreco && atual?.lucroUnit != null && atual.lucroUnit < 0;
  linhas.push({
    nome: 'Resultado',
    projeto: comPreco(atual?.lucroUnit),
    mes: comPreco(atual?.mensal.lucro),
    operador: 'igual',
    campo: resultadoNegativo ? 'telha' : 'mint',
    total: true,
    perda: resultadoNegativo,
  });

  return linhas;
}
